import React, { useState } from 'react'
import { Alert, Button, Flex, Input, Select, Typography, Upload } from 'antd'
import { DeleteOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { Configuration, withFormLocale } from '../public-form'
import { assetUrl } from '../api'
import { resizeLogo } from '../resize-logo'

type BrandingContentControlsProps = {
    value: Configuration
    set: (patch: Partial<Configuration>) => void
    onUpload?: (file: File) => Promise<string>
    /** Only the logo and brand name, for a style that is shared by several forms (each keeps its own texts). */
    brandOnly?: boolean
}
export function BrandingContentControls({ value, set, onUpload, brandOnly = false }: BrandingContentControlsProps) {
    const { t } = useTranslation()
    const [uploading, setUploading] = useState(false)
    const [uploadError, setUploadError] = useState<string>()
    const field = (label: string, key: 'brandName' | 'heading' | 'introduction' | 'buttonLabel') => (
        <label className="block">
            <Typography.Text>{t(label)}</Typography.Text>
            {key === 'introduction' ? (
                <Input.TextArea
                    className="mt-1"
                    rows={3}
                    value={value[key]}
                    maxLength={2000}
                    onChange={(e) => set({ [key]: e.target.value })}
                />
            ) : (
                <Input
                    className="mt-1"
                    aria-label={t(label)}
                    value={value[key]}
                    status={!value[key].trim() ? 'error' : undefined}
                    aria-required
                    maxLength={key === 'heading' ? 180 : key === 'buttonLabel' ? 60 : 120}
                    onChange={(e) =>
                        set({
                            [key]: e.target.value,
                            ...(key === 'buttonLabel' ? { buttonTextMode: 'custom' as const } : {}),
                        })
                    }
                />
            )}
            {key !== 'introduction' && !value[key].trim() && (
                <Typography.Text
                    type="danger"
                    role="alert"
                >
                    {t('This field is required.')}
                </Typography.Text>
            )}
        </label>
    )
    const logoControls = (
        <Flex
            vertical
            gap="small"
            className="public-form-logo-controls"
        >
            <Typography.Text>{t('Logo')}</Typography.Text>
            {onUpload ? (
                <Upload
                    listType="picture-card"
                    className="public-form-logo-upload"
                    aria-label={t(value.logoUrl ? 'Replace logo' : 'Upload logo')}
                    accept="image/png,image/jpeg,image/webp"
                    showUploadList={false}
                    disabled={uploading}
                    beforeUpload={(file) => {
                        if (file.size > 2 * 1024 * 1024) {
                            setUploadError(t('Choose a PNG, JPG, or WebP under 2 MB.'))
                            return false
                        }
                        setUploading(true)
                        setUploadError(undefined)
                        resizeLogo(file)
                            .then(onUpload)
                            .then((logoUrl) => set({ logoUrl }))
                            .catch((error: unknown) => {
                                const errors = isAxiosError<{ errors?: Record<string, string[]> }>(error)
                                    ? error.response?.data?.errors?.logo
                                    : undefined
                                setUploadError(
                                    errors?.join(' ') ||
                                        t(
                                            !isAxiosError(error) && error instanceof Error
                                                ? error.message
                                                : 'Could not upload logo'
                                        )
                                )
                            })
                            .finally(() => setUploading(false))
                        return false
                    }}
                >
                    <div className="relative h-full w-full">
                        <button
                            type="button"
                            disabled={uploading}
                            aria-label={t(value.logoUrl ? 'Replace logo' : 'Upload logo')}
                            className="relative flex h-full w-full cursor-pointer flex-col items-center justify-center gap-2 border-0 bg-transparent"
                        >
                            {uploading ? (
                                <LoadingOutlined />
                            ) : value.logoUrl ? (
                                <img
                                    src={assetUrl(value.logoUrl)}
                                    alt={t('Logo')}
                                    className="absolute inset-0 h-full w-full object-contain p-3 pb-7"
                                />
                            ) : (
                                <PlusOutlined />
                            )}
                            <Typography.Text
                                className={value.logoUrl && !uploading ? 'absolute bottom-1 text-xs' : 'text-xs'}
                            >
                                {t(uploading ? 'Uploading logo…' : value.logoUrl ? 'Replace' : 'Upload logo')}
                            </Typography.Text>
                        </button>
                        {value.logoUrl && !uploading && (
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                aria-label={t('Remove logo')}
                                className="absolute right-0 top-0"
                                onKeyDown={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    setUploadError(undefined)
                                    set({ logoUrl: null })
                                }}
                            />
                        )}
                    </div>
                </Upload>
            ) : (
                <Typography.Text type="secondary">
                    {t('You can upload a logo after creating the campaign.')}
                </Typography.Text>
            )}
            {onUpload && <Typography.Text type="secondary">{t('PNG, JPG, or WebP. Maximum 2 MB.')}</Typography.Text>}
            {uploadError && (
                <Alert
                    type="error"
                    message={uploadError}
                />
            )}
        </Flex>
    )
    return (
        <Flex
            vertical
            gap="middle"
            className="public-form-content-controls"
        >
            {logoControls}
            {field('Brand name', 'brandName')}
            {!brandOnly && (
                <BrandingTexts
                    value={value}
                    set={set}
                    field={field}
                />
            )}
        </Flex>
    )
}

function BrandingTexts({
    value,
    set,
    field,
}: {
    value: Configuration
    set: (patch: Partial<Configuration>) => void
    field: (label: string, key: 'brandName' | 'heading' | 'introduction' | 'buttonLabel') => React.ReactNode
}) {
    const { t } = useTranslation()
    return (
        <>
            {field('Heading', 'heading')}
            {field('Introduction', 'introduction')}
            {field('Button label', 'buttonLabel')}
            <label className="block">
                <Typography.Text>{t('Language')}</Typography.Text>
                <Select
                    className="mt-1 w-full"
                    aria-label={t('Language')}
                    value={value.locale}
                    onChange={(locale) => set(withFormLocale(value, locale))}
                    options={[
                        { value: 'nl_NL', label: 'Nederlands' },
                        { value: 'en_US', label: 'English' },
                    ]}
                />
            </label>
        </>
    )
}
